/**
 * CSV utility functions for parsing and generating CSV data
 */

export interface IbrsRateData {
  sending_ppo: string;
  receiving_ppo: string;
  product: string;
  ibrs_rate_kg: number;
  ibrs_rate_item: number;
}

/**
 * Parse CSV string into array of objects
 * @param csvString - The CSV string to parse
 * @returns Array of objects with CSV data
 */
export function parseCSV(csvString: string): IbrsRateData[] {
  const lines = csvString.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('CSV must have at least a header row and one data row');
  }

  const headers = lines[0].split(',').map(header => header.trim());
  
  // Validate required headers
  const requiredHeaders = ['sending_ppo', 'receiving_ppo', 'product', 'ibrs_rate_kg', 'ibrs_rate_item'];
  const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
  
  if (missingHeaders.length > 0) {
    throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
  }

  const data: IbrsRateData[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines
    
    const values = line.split(',').map(value => value.trim());
    
    if (values.length !== headers.length) {
      throw new Error(`Row ${i + 1} has ${values.length} values but expected ${headers.length}`);
    }

    const row: any = {};
    headers.forEach((header, index) => {
      row[header] = values[index];
    });

    // Validate and convert numeric values
    const ibrs_rate_kg = parseFloat(row.ibrs_rate_kg);
    const ibrs_rate_item = parseFloat(row.ibrs_rate_item);
    
    if (isNaN(ibrs_rate_kg) || isNaN(ibrs_rate_item)) {
      throw new Error(`Row ${i + 1}: Invalid numeric values for IBRS rates`);
    }

    data.push({
      sending_ppo: row.sending_ppo,
      receiving_ppo: row.receiving_ppo,
      product: row.product,
      ibrs_rate_kg,
      ibrs_rate_item
    });
  }

  return data;
}

/**
 * Convert IBRS rate data to CSV string
 * @param data - Array of IBRS rate data objects
 * @returns CSV string
 */
export function convertToCSV(data: IbrsRateData[]): string {
  if (data.length === 0) {
    return 'sending_ppo,receiving_ppo,product,ibrs_rate_kg,ibrs_rate_item\n';
  }

  const headers = ['sending_ppo', 'receiving_ppo', 'product', 'ibrs_rate_kg', 'ibrs_rate_item'];
  const csvRows = [headers.join(',')];

  data.forEach(row => {
    const values = [
      row.sending_ppo,
      row.receiving_ppo,
      row.product,
      row.ibrs_rate_kg.toString(),
      row.ibrs_rate_item.toString()
    ];
    csvRows.push(values.join(','));
  });

  return csvRows.join('\n');
}

/**
 * Generate sample CSV data for IBRS rates
 * @param ppo - The PPO code to use
 * @returns Sample CSV string
 */
export function generateSampleCSV(ppo: string): string {
  const sampleData: IbrsRateData[] = [
    {
      sending_ppo: ppo,
      receiving_ppo: ppo,
      product: 'normal',
      ibrs_rate_kg: 1,
      ibrs_rate_item: 1
    },
    {
      sending_ppo: ppo,
      receiving_ppo: ppo,
      product: 'bulky',
      ibrs_rate_kg: 1,
      ibrs_rate_item: 1
    }
  ];

  return convertToCSV(sampleData);
}

/**
 * Download CSV file
 * @param csvContent - CSV string content
 * @param filename - Name of the file to download
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
} 